/**
 * ShareableTasteBadge
 *
 * Standalone badge design optimized for image generation (1080x1080).
 * Used to create shareable images for social media.
 */
import { forwardRef } from 'react'
import { Box, Text, VStack, HStack, Heading, Badge } from '@chakra-ui/react'
import {
  generateTasteAuraGradient,
  FavoriteCuisine,
} from '@/utils/cuisineColorMapping'
import {
  getRarityTier,
  getRarityColors,
  getRarityDisplayName,
} from '@/utils/rarityCalculator'
import type { TasteInsights } from '@/types/tasteInsights'

export interface ShareableTasteBadgeProps {
  /** User's TasteInsights data */
  tasteInsights: TasteInsights
  /** User's display name */
  userName: string
  /** User's favorite cuisines for gradient */
  favoriteCuisines?: FavoriteCuisine[]
  /** Size in pixels (default 1080 for Instagram) */
  size?: number
  /** Show the Paire branding */
  showBranding?: boolean
}

/**
 * ShareableTasteBadge - A visual card for sharing TasteInsights
 * This component is designed to be captured as an image using html-to-image
 */
export const ShareableTasteBadge = forwardRef<HTMLDivElement, ShareableTasteBadgeProps>(
  function ShareableTasteBadge(
    {
      tasteInsights,
      userName,
      favoriteCuisines = [],
      size = 1080,
      showBranding = true,
    },
    ref
  ) {
    // Get rarity from insights (LLM-assigned or fallback)
    const rarityTier = getRarityTier(tasteInsights)
    const rarityColors = getRarityColors(rarityTier)
    const rarityName = getRarityDisplayName(rarityTier)

    // Generate gradient from cuisines
    const gradient = generateTasteAuraGradient(favoriteCuisines)

    // Scale factor for responsive sizing (base is 1080px)
    const scale = size / 1080

    return (
      <Box
        ref={ref}
        width={`${size}px`}
        height={`${size}px`}
        background={gradient}
        position="relative"
        overflow="hidden"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        {/* Background pattern overlay */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.1}
          backgroundImage="radial-gradient(circle at 25% 25%, white 1px, transparent 1px)"
          backgroundSize={`${40 * scale}px ${40 * scale}px`}
        />

        {/* Vignette effect */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          background="radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 100%)"
        />

        {/* Content */}
        <VStack
          position="relative"
          height="100%"
          justify="center"
          align="center"
          px={`${80 * scale}px`}
          py={`${100 * scale}px`}
          spacing={`${40 * scale}px`}
        >
          {/* User name */}
          <Text
            fontSize={`${36 * scale}px`}
            fontWeight="medium"
            color="whiteAlpha.800"
            textTransform="uppercase"
            letterSpacing={`${4 * scale}px`}
          >
            {userName}
          </Text>

          {/* Taste Tribe with emojis */}
          <VStack spacing={`${20 * scale}px`}>
            <Text fontSize={`${80 * scale}px`}>{tasteInsights.tasteTribeEmojis}</Text>
            <Heading
              fontSize={`${64 * scale}px`}
              fontWeight="bold"
              color="white"
              textAlign="center"
              textShadow={`0 ${4 * scale}px ${20 * scale}px rgba(0,0,0,0.3)`}
              lineHeight="1.2"
            >
              {tasteInsights.tasteTribe}
            </Heading>
          </VStack>

          {/* Insight snippet */}
          <Text
            fontSize={`${32 * scale}px`}
            color="whiteAlpha.900"
            fontStyle="italic"
            textAlign="center"
            maxWidth={`${800 * scale}px`}
            lineHeight="1.5"
            textShadow={`0 ${2 * scale}px ${10 * scale}px rgba(0,0,0,0.2)`}
          >
            "{tasteInsights.insightSnippet}"
          </Text>

          {/* Rarity badge */}
          <HStack spacing={`${16 * scale}px`}>
            <Badge
              bg={rarityColors.bg}
              color={rarityColors.textColor}
              px={`${24 * scale}px`}
              py={`${12 * scale}px`}
              borderRadius={`${30 * scale}px`}
              fontSize={`${24 * scale}px`}
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing={`${2 * scale}px`}
              boxShadow={
                rarityTier === 'ultra rare' || rarityTier === 'super rare'
                  ? `0 0 ${20 * scale}px ${rarityColors.glow}`
                  : undefined
              }
            >
              {rarityName}
            </Badge>
          </HStack>

          {/* Cuisine tags (if available) */}
          {favoriteCuisines.length > 0 && (
            <HStack
              spacing={`${12 * scale}px`}
              flexWrap="wrap"
              justify="center"
              maxWidth={`${900 * scale}px`}
            >
              {favoriteCuisines.slice(0, 5).map((cuisine) => (
                <Box
                  key={cuisine.name}
                  bg="var(--card-surface-2)"
                  px={`${20 * scale}px`}
                  py={`${10 * scale}px`}
                  borderRadius={`${20 * scale}px`}
                  border="1px solid"
                  borderColor="var(--card-divider)"
                >
                  <Text
                    fontSize={`${20 * scale}px`}
                    color="white"
                    fontWeight="medium"
                  >
                    {cuisine.flag} {cuisine.name}
                  </Text>
                </Box>
              ))}
            </HStack>
          )}
        </VStack>

        {/* Branding footer */}
        {showBranding && (
          <HStack
            position="absolute"
            bottom={`${40 * scale}px`}
            left={0}
            right={0}
            justify="center"
            spacing={`${12 * scale}px`}
          >
            <Text
              fontSize={`${24 * scale}px`}
              color="whiteAlpha.600"
              fontWeight="medium"
              letterSpacing={`${2 * scale}px`}
            >
              Discover your TasteInsights at
            </Text>
            <Text
              fontSize={`${24 * scale}px`}
              color="whiteAlpha.800"
              fontWeight="bold"
              letterSpacing={`${1 * scale}px`}
            >
              paire.io
            </Text>
          </HStack>
        )}
      </Box>
    )
  }
)

/**
 * Mini version for inline display and previews
 */
export function ShareableTasteBadgeMini({
  tasteInsights,
  favoriteCuisines = [],
}: Pick<ShareableTasteBadgeProps, 'tasteInsights' | 'favoriteCuisines'>) {
  const gradient = generateTasteAuraGradient(favoriteCuisines)
  const rarityTier = getRarityTier(tasteInsights)
  const rarityColors = getRarityColors(rarityTier)

  return (
    <Box
      width="200px"
      height="200px"
      background={gradient}
      borderRadius="var(--r-card-compact)"
      position="relative"
      overflow="hidden"
    >
      <VStack
        height="100%"
        justify="center"
        align="center"
        px={4}
        spacing={2}
      >
        <Text fontSize="display">{tasteInsights.tasteTribeEmojis}</Text>
        <Text
          fontSize="sm"
          fontWeight="bold"
          color="white"
          textAlign="center"
          noOfLines={2}
        >
          {tasteInsights.tasteTribe}
        </Text>
        <Badge
          bg={rarityColors.bg}
          color={rarityColors.textColor}
          fontSize="xs"
          px={2}
          py={0.5}
          borderRadius="full"
        >
          {getRarityDisplayName(rarityTier)}
        </Badge>
      </VStack>
    </Box>
  )
}
